import { DateTime } from '@ksyos/date-time';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IPatient } from '../../../../../../common/interfaces/patient';
import type { IReduxState } from '../../../../../configureStore';
import type { IEmbloomQuestionnaire } from '../../../../interfaces/embloomQuestionnaire';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { CustomControlStatus } from '../../../../../../common/enums/customControlStatus';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { StatusLabelStatus } from '../../../../../../common/enums/statusLabelStatus';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { FormParagraph, InlineElement, LinkOnClick, UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { FormSection } from '../../../../elements/forms/formSection/FormSection';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ResendInviteModal } from '../../../../elements/ResendInviteModal';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { GgzoEmblV1FollowingSteps } from './GgzoEmblV1FollowingSteps';

export function GgzoEmblV1RequestRead(props: ICustomControlRequestReadProps) {
    const [questionnaire, setQuestionnaire] = useState<IEmbloomQuestionnaire | undefined>();
    const [showEditEmailModal, setShowEditEmailModal] = useState(false);
    const [canEditEmail, setCanEditEmail] = useState(false);
    const sessionUserId = useSelector((state: IReduxState) => state.authentication.id);
    const [patient, setPatient] = useState<IPatient>();

    useEffect(() => {
        if (props.request.customControlData.vragenlijst) {
            loadQuestionnaire(props.request.customControlData.vragenlijst);
        }
    }, []);

    useEffect(() => {
        loadProcedure();
        loadPatient();
    }, [props.request.id]);

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isExaminationRequest
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => {
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || !!props.itemState.initialIsOpen,
                    });
                }}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                // TODO: aanpassen met nieuwe datamodel changes of tasks
                statusLabel={
                    props.request.authorization?.canAddExaminationDiagnosticReport
                        ? StatusLabelStatus.toGrade
                        : undefined
                }
                title={questionnaire ? `${questionnaire.label} ${translateKey('request.request').toLowerCase()}` : ''}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <FormSection isBorderLess={props.request.status !== RequestStatus.requested}>
                    <ProfileImages
                        alternativeLabelText={'professionEnum.generalPractitioner'}
                        organization={props.request.draftCareProviderOrganization}
                        practitioner={props.request.draftCareProviderPractitioner}
                        request={props.request}
                        showAlsoWhenNotRequested
                    />
                </FormSection>

                {props.request.customControlData.emailadres && (
                    <HorizontalFormGroupRead>
                        <FormLabel>E-mailadres patiënt</FormLabel>
                        <InputContainer>
                            {canEditEmail && (
                                <FormParagraph>
                                    <InlineElement className="mr-3">
                                        {props.request.customControlData.emailadres}
                                    </InlineElement>
                                    <LinkOnClick
                                        className="btn-transparent"
                                        onClick={() => setShowEditEmailModal(true)}
                                    >
                                        Verstuur naar ander e-mailadres
                                    </LinkOnClick>
                                </FormParagraph>
                            )}
                            {!canEditEmail && (
                                <UserTextParagraph>{props.request.customControlData.emailadres}</UserTextParagraph>
                            )}
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {(props.request.selectedDiagnosticReportProviderPractitionerId ||
                    props.request.selectedDiagnosticReportProviderOrganizationId) && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('request.sendTo')}</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>
                                {props.request.selectedDiagnosticReportProviderPractitionerId
                                    ? getDisplayName(props.request.selectedDiagnosticReportProviderPractitioner!)
                                    : props.request.selectedDiagnosticReportProviderOrganization!.name}
                            </UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <HorizontalFormGroupRead>
                    <FormLabel>Nameting sturen</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>{getNameting()}</UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>

                <ProfileImages request={props.request} />

                <GgzoEmblV1FollowingSteps />
                <RequestReadButtons {...props} />

                {patient && showEditEmailModal && (
                    <ResendInviteModal
                        onClose={async () => {
                            setShowEditEmailModal(false);
                            await props.reloadRequest();
                        }}
                        patient={patient}
                        request={props.request}
                    />
                )}
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideRequest')}
                    labelMore={translateKey('request.showRequest')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );

    async function loadQuestionnaire(questionnaireId: string) {
        const result = await getJson(`/api/embloomQuestionnaires/${questionnaireId}`);
        setQuestionnaire(result.json);
    }

    async function loadProcedure() {
        if (props.request.status !== RequestStatus.draft) {
            const result = await getJson(`/api/requests/${props.request.id}/procedure`);
            setCanEditEmail(
                props.request.status === RequestStatus.active &&
                    result.json?.customControlStatus === CustomControlStatus.withPatient &&
                    sessionUserId === props.request.requesterPractitionerId
            );
        } else {
            setCanEditEmail(false);
        }
    }

    function getNameting() {
        if (
            props.request.customControlData.heeftNameting &&
            props.request.customControlData.nameting &&
            props.request.customControlData.nametingOp
        ) {
            return `Ja, na ${props.request.customControlData.nameting} ${
                props.request.customControlData.nameting === 1 ? 'week' : 'weken'
            } (${DateTime.from(props.request.customControlData.nametingOp).format('D MMMM YYYY')})`;
        }

        return 'Nee';
    }

    async function loadPatient() {
        const url = `/api/patients/${props.request.patientId}`;
        const result = await getJson(url);
        setPatient(result.json);
    }
}
